import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  getMerchantPortalAdminToken,
  merchantPortalKycAdminLogin,
  setMerchantPortalAdminToken,
} from "../../services/api";
import { friendlyError } from "../../utils/friendlyError";
import { LanguagePicker } from "../../components/LanguagePicker";
import { t } from "../../i18n";

/**
 * Same credentials as the driver KYC admin portal: POST /api/kyc-admin/login.
 * Token is stored in sessionStorage only (not mixed with merchant JWT).
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (getMerchantPortalAdminToken()) {
    return <Navigate to="/admin/merchants" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await merchantPortalKycAdminLogin(username.trim(), password);
      const token = data?.token;
      if (!token) throw new Error(t("login.unexpected_response"));
      setMerchantPortalAdminToken(token);
      navigate("/admin/merchants", { replace: true });
    } catch (err) {
      setError(friendlyError(err, t("login.failed")));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "var(--primary-darker)",
      }}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth: 400,
          padding: "28px 24px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <LanguagePicker style={{ fontSize: 12 }} />
        </div>
        <h1 style={{ marginTop: 0, fontSize: "1.35rem" }}>{t("admin.login.title")}</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 0 }}>{t("admin.login.lead")}</p>
        <form onSubmit={onSubmit}>
          <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>{t("admin.login.username")}</label>
          <input
            value={username}
            onChange={(ev) => setUsername(ev.target.value)}
            autoComplete="username"
            style={{
              width: "100%",
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid var(--border-color)",
              background: "var(--primary-light)",
              color: "var(--text-primary)",
            }}
          />
          <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>{t("login.password_label")}</label>
          <input
            type="password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            autoComplete="current-password"
            style={{
              width: "100%",
              marginBottom: 16,
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid var(--border-color)",
              background: "var(--primary-light)",
              color: "var(--text-primary)",
            }}
          />
          {error ? (
            <p style={{ color: "var(--error-text)", fontSize: 14, marginTop: 0 }}>{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={loading || !username.trim() || !password}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: 14,
              border: "none",
              background: loading ? "#a59eae" : "var(--accent-primary)",
              color: "#ffffff",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? t("login.signing_in") : t("login.sign_in")}
          </button>
        </form>
        <p style={{ marginTop: 20, marginBottom: 0, fontSize: 13, textAlign: "center" }}>
          <Link to="/login" style={{ color: "var(--accent-muted)" }}>
            {t("admin.login.merchant_sign_in")}
          </Link>
        </p>
      </div>
    </div>
  );
}
